// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Download from "@insite/mobius/Icons/Download";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import constants from "@store/constants";
import { AddressModel } from "@store/Library/Pages/EventDetailsPage";
import { linkStyles } from "@store/Themes/common/mixins/link";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

export type CalendarServices = "apple" | "google" | "outlook" | "outlookcom" | "office365" | "yahoo" | "stream";

interface Props {
    eventTitle: string;
    startDate: Date;
    eventSummary?: string;
    endDate?: Date;
    isAllDay?: boolean;
    dateTimezone?: string;
    isLocalBranch?: boolean;
    addressWrapper?: AddressModel[];
    extendedStyles?: EventDetailsAddToCalendarStyles;
}

export interface EventDetailsAddToCalendarStyles {
    link?: LinkPresentationProps;
    addToCalendarModal?: ModalPresentationProps;
    btnWrapper?: ButtonPresentationProps;
    linkWrapper?: ButtonPresentationProps;
    downloadLink?: LinkPresentationProps;
}

export const eventDetailsAddToCalendarStyles: EventDetailsAddToCalendarStyles = {
    link: {
        typographyProps: {
            css: css`
                font-weight: ${x => x.theme.design.typography.weight.semibold};
            `,
        },
        css: css`
            ${linkStyles("primary", "primary.dark", true, false)}
            margin-top: ${pxToRem(5)};
        `,
    },
    addToCalendarModal: {
        cssOverrides: {
            modalContent: css`
                display: flex;
                flex-wrap: wrap;
                justify-content: flex-start;
                align-items: center;
            `,
        },
    },
    btnWrapper: {
        css: css`
            padding: ${pxToRem(10)};
            text-align: center;
            ${breakpointValues(["100%", "50%", null, "calc(100% / 3)"], ["width"])}
        `,
    },
    linkWrapper: {
        css: css`
            padding-top: ${pxToRem(20)};
            border-top: 1px solid ${getColor("common.border")};
            margin-top: ${pxToRem(20)};
            width: 100%;
            text-align: center;
        `,
    },
    downloadLink: {
        icon: {
            iconProps: {
                src: Download,
                size: 16,
                color: "primary",
            },
        },
        typographyProps: {
            css: css`
                font-weight: ${x => x.theme.design.typography.weight.semibold};
            `,
        },
        css: css`
            ${linkStyles("primary", "primary.dark", true, false)}
        `,
    },
};

const EventDetailsAddToCalendar = ({
    eventTitle,
    startDate,
    endDate,
    isAllDay,
    dateTimezone,
    isLocalBranch,
    addressWrapper,
    eventSummary,
    extendedStyles,
}: Props) => {
    const styles = useMergeProps(eventDetailsAddToCalendarStyles, extendedStyles);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [websiteUrl, setWebsiteUrl] = useState<string | undefined>();
    const locationFinderUrl = usePageLinkByPageType(constants.pageType.locationFinder)?.url;
    const baseUrl = `https://calndr.link/d/event/?`;

    useEffect(() => {
        setWebsiteUrl(window.location.origin);
    }, []);

    if (!websiteUrl) {
        return null;
    }

    const getDateWithoutTz = (date: any) => {
        let dateToFormat;
        if (typeof startDate === "string") {
            dateToFormat = date;
        } else {
            const tempDate = new Date(date);
            dateToFormat = tempDate.toISOString();
        }

        return dateToFormat.slice(0, -1);
    };

    const eventValues: any = {
        title: eventTitle,
        start: getDateWithoutTz(startDate),
        end: endDate ? getDateWithoutTz(endDate) : undefined,
        all_day: isAllDay ? "1" : "0", // eslint-disable-line camelcase
        timezone: dateTimezone ?? undefined,
        description: eventSummary ?? undefined,
        location:
            !isLocalBranch && addressWrapper?.length
                ? addressWrapper?.[0].fields.addressLabel
                : `${websiteUrl}${locationFinderUrl}`,
    };

    const buildAddToCalUrl = (service: CalendarServices) => {
        const params = [];
        eventValues.service = service;

        for (const key in eventValues) {
            if (eventValues[key]) {
                params.push(`${encodeURIComponent(key)}=${encodeURIComponent(eventValues[key])}`);
            }
        }

        const paramString = params.join("&");
        const url = `${baseUrl}${paramString}`;

        return window.open(url, "_blank");
    };

    const onClick = (service: CalendarServices) => {
        buildAddToCalUrl(service);
        setModalIsOpen(false);
    };

    return (
        <>
            <Link {...styles.link} onClick={() => setModalIsOpen(true)}>
                {translate("Add to my calendar")}
            </Link>
            <Modal
                {...styles.addToCalendarModal}
                headline={translate("Add to my calendar")}
                isOpen={modalIsOpen}
                handleClose={() => setModalIsOpen(false)}
            >
                <StyledWrapper {...styles.btnWrapper}>
                    <Button onClick={() => onClick("google")}>{translate("Google Calendar")}</Button>
                </StyledWrapper>
                <StyledWrapper {...styles.btnWrapper}>
                    <Button onClick={() => onClick("apple")}>{translate("Apple")}</Button>
                </StyledWrapper>
                <StyledWrapper {...styles.btnWrapper}>
                    <Button onClick={() => onClick("outlook")}>{translate("Outlook")}</Button>
                </StyledWrapper>
                <StyledWrapper {...styles.btnWrapper}>
                    <Button onClick={() => onClick("outlookcom")}>{translate("Outlook.com")}</Button>
                </StyledWrapper>
                <StyledWrapper {...styles.btnWrapper}>
                    <Button onClick={() => onClick("office365")}>{translate("Office365")}</Button>
                </StyledWrapper>
                <StyledWrapper {...styles.btnWrapper}>
                    <Button onClick={() => onClick("yahoo")}>{translate("Yahoo")}</Button>
                </StyledWrapper>
                <StyledWrapper {...styles.linkWrapper}>
                    <Link {...styles.downloadLink} onClick={() => onClick("stream")}>
                        {translate("Download event file")}
                    </Link>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default EventDetailsAddToCalendar;
